<template>
    <BaseDialog v-model:open="isOpen" :title="t('chat.managerReview.title', { name: revieweeName || t('chat.managerReview.fallbackName') })">
        <div v-if="draft" class="flex flex-col gap-5">
            <template v-if="!submitted">
                <p class="body-small text-ink-muted" data-testid="manager-review-description">{{ t("chat.managerReview.dialogDescription") }}</p>

                <div v-if="draft.loaded_from_workday" class="flex flex-col gap-2 rounded-md bg-surface-sunken p-3" data-testid="manager-review-loaded-from-workday">
                    <p class="body-small text-ink-muted">{{ t("chat.managerReview.loadedFromWorkday") }}</p>
                    <div>
                        <BaseButton theme="secondary" data-testid="redraft-manager-review" :disabled="loading || redrafting" @click="emit('redraft')">
                            {{ t("chat.managerReview.redraft") }}
                        </BaseButton>
                    </div>
                    <div v-if="redraftError" class="body-small text-notification-error-text" role="alert" data-testid="redraft-manager-review-error">{{ redraftError }}</div>
                </div>

                <section v-for="section in localDraft.sections" :key="section.section_id" class="flex flex-col gap-2">
                    <h4 class="body-semibold text-ink">{{ section.label }}</h4>

                    <div class="flex flex-col gap-1">
                        <div class="body-small text-control-label">{{ t("chat.workday.sectionComment", { label: section.label }) }}</div>
                        <BaseTextarea
                            v-model:input="section.comment"
                            rows="3"
                            :disabled="loading"
                            :aria-label="t('chat.workday.sectionComment', { label: section.label })"
                            :placeholder="t('chat.workday.sectionCommentPlaceholder', { label: section.label })"
                            class="leading-[normal] border border-transparent rounded-md p-3 focus-field" />
                    </div>
                    <p
                        v-if="repeatsConfidential(section.section_id)"
                        class="body-small text-notification-error-text"
                        :data-testid="`manager-review-confidential-${section.section_id}`">
                        {{ confidentialOverlapNotice }}
                    </p>

                    <div v-if="sectionRatingScope(section)" class="flex flex-col gap-1" :data-testid="`manager-review-section-rating-${section.section_id}`">
                        <div class="body-small text-control-label">{{ t("chat.managerReview.sectionRating", { label: section.label }) }}</div>
                        <BaseSelect
                            v-model="section.rating"
                            :options="ratingChoices(sectionRatingScope(section))"
                            :disabled="loading"
                            :placeholder="t('chat.managerReview.chooseRating')"
                            :aria-label="t('chat.managerReview.sectionRating', { label: section.label })" />
                    </div>
                </section>

                <section class="flex flex-col gap-1">
                    <h4 class="body-semibold text-ink">{{ t("chat.workday.overallComment") }}</h4>
                    <BaseTextarea
                        v-model:input="localDraft.overall_comment"
                        rows="3"
                        :disabled="loading"
                        :aria-label="t('chat.workday.overallComment')"
                        :placeholder="t('chat.workday.overallCommentPlaceholder')"
                        class="leading-[normal] border border-transparent rounded-md p-3 focus-field" />
                    <p v-if="repeatsConfidential('overall')" class="body-small text-notification-error-text" data-testid="manager-review-confidential-overall">
                        {{ confidentialOverlapNotice }}
                    </p>
                </section>

                <!-- Only where the tenant's own template says a manager may rate, and only with
                     the ids it supplied. Nadia never suggests a rating; the manager picks one. -->
                <section v-if="overallRatingScope" class="flex flex-col gap-2" data-testid="manager-review-overall-rating">
                    <h4 class="body-semibold text-ink">{{ t("chat.managerReview.overallRating") }}</h4>
                    <BaseSelect
                        v-model="localDraft.overall_rating"
                        :options="overallRatingChoices"
                        :disabled="loading"
                        :placeholder="t('chat.managerReview.chooseRating')"
                        :aria-label="t('chat.managerReview.overallRating')" />
                    <p v-if="overallRatingMissing" class="body-small text-ink-muted">{{ t("chat.managerReview.overallRatingRequired") }}</p>
                </section>

                <p v-if="sectionRatingMissing" class="body-small text-ink-muted">{{ t("chat.managerReview.sectionRatingRequired") }}</p>

                <!-- Required comments the template still needs (LAB-2201): filled by the draft or
                     by what is already in Workday. The server re-checks at submit. -->
                <p v-if="missingRequiredComments.length" class="body-small text-ink-muted" data-testid="manager-review-required-comments">
                    {{ t("chat.managerReview.requiredCommentsMissing", { names: missingRequiredComments.join(", ") }) }}
                </p>

                <div v-if="noticeMessage" class="body-small text-notification-error-text" role="alert">{{ noticeMessage }}</div>
            </template>

            <div v-else class="flex flex-col items-center gap-3 py-4 text-center" data-testid="manager-review-submitted">
                <i class="bi bi-check-circle text-2xl text-chip-status-done-text" aria-hidden="true"></i>
                <p class="nadia-chat text-ink">
                    {{ t("chat.managerReview.submitted", { name: revieweeName || t("chat.workday.fallbackReviewee") }) }}
                </p>
                <!-- The connection's own post-submit note when it sets one, else the default: what
                     Workday still expects of the manager after a push (LAB-2202). -->
                <p class="body-small text-ink-muted" data-testid="manager-review-confirmation-note">
                    {{ pushConfirmationNote || t("chat.managerReview.pushConfirmationDefault") }}
                </p>
                <div v-if="sentComments.length" class="flex w-full flex-col gap-3 text-left" data-testid="manager-review-sent">
                    <h4 class="body-semibold text-ink">{{ t("chat.managerReview.sentHeading") }}</h4>
                    <section v-for="sent in sentComments" :key="sent.label" class="flex flex-col gap-1">
                        <div class="body-small text-control-label">{{ sent.label }}</div>
                        <p class="body-small whitespace-pre-wrap text-ink">{{ sent.comment }}</p>
                    </section>
                </div>
                <BaseButton theme="primary" @click="isOpen = false">{{ t("chat.managerReview.done") }}</BaseButton>
            </div>
        </div>
        <template v-if="draft && !submitted" #footer>
            <BaseButton theme="tertiary" :disabled="loading" @click="isOpen = false">{{ t("common.cancel") }}</BaseButton>
            <BaseButton theme="primary" data-testid="submit-manager-review-dialog" :disabled="submitDisabled" class="flex items-center justify-center gap-1" @click="handleSubmit">
                <LoadingSpinner v-if="loading" class="h-4" />{{ t("chat.workday.submit") }}
            </BaseButton>
        </template>
    </BaseDialog>
</template>

<script setup lang="ts">
import { ChatAction } from "~vue/chatActions";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseSelect from "~vue/components/BaseSelect.vue";
import BaseTextarea from "~vue/components/form/BaseTextarea.vue";
import { useActionHandler } from "~vue/composables/useActionHandler";
import { useDeferredLoading } from "~vue/composables/useDeferredLoading";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { PERFORMANCE_REVIEW_EVENT_PREFIX } from "~vue/Moments/constants";
import { resolveActionRefusal } from "~vue/utils/actionRefusal";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { computed, inject, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

interface CardSection {
    section_id: string;
    label: string;
    comment: string;
    rating: string;
    rating_scope: string;
    workday_comment?: string;
    workday_rating?: string;
    comment_required?: boolean;
}
interface CardDraft {
    review_ref: string;
    overall_comment: string;
    overall_rating: string;
    workday_overall_comment?: string;
    sections: CardSection[];
    loaded_from_workday?: boolean;
    overall_comment_required?: boolean;
    confidential_overlap?: string[];
}
interface RatingChoice {
    review_rating_id: string;
    label: string;
}
interface RatingChoiceOption {
    key: string;
    label: string;
}
/** One scope of the tenant's discovered template, as the server authored it (LAB-1264). */
export interface RatingScope {
    scope: string;
    label: string;
    can_rate: boolean;
    rating_required: boolean;
    input_kind: string;
    options: RatingChoice[];
}

interface Props {
    draft: CardDraft | null;
    revieweeName?: string | null;
    messageId: number;
    ratingOptions?: RatingScope[];
    /** The connection's post-submit note, shown verbatim; empty shows the translated default. */
    pushConfirmationNote?: string;
    redrafting?: boolean;
    redraftError?: string;
}

const props = defineProps<Props>();
const emit = defineEmits<{ redraft: [] }>();
const isOpen = defineModel<boolean>("open", { default: false });
const { t } = useI18n();

const { execute } = useActionHandler();
const coachingSessionUuid = inject<string | undefined>("coachingSessionUuid", undefined);
const { loading, start: startLoading, stop: stopLoading } = useDeferredLoading();

const submitted = ref(false);
// The comments exactly as submitted, shown after the push so the manager can check them against Workday.
const sentComments = ref<Array<{ label: string; comment: string }>>([]);
// Not `loading`: that one is deferred by 200ms for flicker, which is long enough to double-submit.
const inFlight = ref(false);
const noticeMessage = ref("");

function cloneDraft(source: CardDraft | null): CardDraft {
    if (!source) {
        return { review_ref: "", overall_comment: "", overall_rating: "", sections: [] };
    }
    const cloned = { overall_rating: "", ...JSON.parse(JSON.stringify(source)) };
    cloned.sections = cloned.sections.map((section: CardSection) => ({ ...section, rating: section.rating ?? "", rating_scope: section.rating_scope ?? "" }));
    return cloned;
}

const localDraft = reactive<CardDraft>(cloneDraft(props.draft));

// A scope the template does not let a manager rate simply has no control -- never an empty picker,
// which would let a manager believe they had rated when the submit would refuse it. A section's
// scope comes from the server (LAB-1538); a section it could not match to the template has none.
function rateableScope(scopeKey: string): RatingScope | null {
    if (!scopeKey) {
        return null;
    }
    return (props.ratingOptions ?? []).find((scope) => scope.scope === scopeKey && scope.can_rate && scope.input_kind === "reference" && scope.options.length > 0) ?? null;
}
function ratingChoices(scope: RatingScope | null): RatingChoiceOption[] {
    return (scope?.options ?? []).map((option) => ({ key: option.review_rating_id, label: option.label }));
}
function sectionRatingScope(section: CardSection): RatingScope | null {
    return rateableScope(section.rating_scope);
}

// Flagged when the draft was built, and kept while the manager edits: a partial rewrite can still carry the wording.
function repeatsConfidential(scope: string): boolean {
    return (props.draft?.confidential_overlap ?? []).includes(scope);
}
const confidentialOverlapNotice = computed(() => t("chat.managerReview.confidentialOverlap", { name: props.revieweeName || t("chat.managerReview.fallbackName") }));

const overallRatingScope = computed<RatingScope | null>(() => rateableScope("overall"));
const overallRatingChoices = computed<RatingChoiceOption[]>(() => ratingChoices(overallRatingScope.value));
const overallRatingMissing = computed(() => Boolean(overallRatingScope.value?.rating_required) && !localDraft.overall_rating);
const sectionRatingMissing = computed(() => localDraft.sections.some((section) => Boolean(sectionRatingScope(section)?.rating_required) && !section.rating));
// The template's required comments still empty (LAB-2201): what the manager typed or what Workday
// already holds both count -- an unchanged Workday comment is never re-sent but still satisfies it.
const missingRequiredComments = computed<string[]>(() => {
    const filled = (comment?: string, workday?: string) => Boolean(comment?.trim() || workday?.trim());
    const missing: string[] = [];
    if (props.draft?.overall_comment_required && !filled(localDraft.overall_comment, props.draft.workday_overall_comment)) {
        missing.push(t("chat.workday.overallComment"));
    }
    for (const section of localDraft.sections) {
        if (section.comment_required && !filled(section.comment, section.workday_comment)) {
            missing.push(section.label);
        }
    }
    return missing;
});

// A redraft replaces the card when it lands, so a submit racing it could be followed by fresh text.
const submitDisabled = computed(
    () => inFlight.value || loading.value || props.redrafting || overallRatingMissing.value || sectionRatingMissing.value || missingRequiredComments.value.length > 0,
);

// Reset the editable copy and state whenever a fresh draft is opened.
watch(
    () => [props.draft, isOpen.value] as const,
    ([draft, open]) => {
        if (open && draft) {
            Object.assign(localDraft, cloneDraft(draft));
            submitted.value = false;
            noticeMessage.value = "";
        }
    },
);

async function handleSubmit() {
    if (inFlight.value) {
        return;
    }
    inFlight.value = true;
    startLoading();
    noticeMessage.value = "";
    logUserInteraction(`${PERFORMANCE_REVIEW_EVENT_PREFIX}submit_manager_review_clicked`, { session_id: coachingSessionUuid }, coachingSessionUuid);

    const submittedDraft: CardDraft = JSON.parse(JSON.stringify(localDraft));
    try {
        const result = await resolveActionRefusal(execute(ChatAction.SUBMIT_MANAGER_REVIEW_DRAFT, { message_id: props.messageId, draft: submittedDraft }, { sync: true }));
        if (result?.payload?.submitted) {
            sentComments.value = [
                ...submittedDraft.sections.map((section) => ({ label: section.label, comment: section.comment.trim() })),
                { label: t("chat.managerReview.overallCommentLabel"), comment: submittedDraft.overall_comment.trim() },
            ].filter((sent) => sent.comment);
            submitted.value = true;
        } else {
            noticeMessage.value = result?.payload?.error ?? t("chat.workday.submitError");
        }
    } catch (e) {
        logError(e);
        noticeMessage.value = t("chat.workday.submitError");
    } finally {
        stopLoading();
        inFlight.value = false;
    }
}
</script>
